import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export type TemplateStatus = 'DRAFT' | 'ACTIVE' | 'RETIRED';

/** Shape mirrors the chit_type_templates entity returned by GET /staff/v1/chit-groups/templates. */
export interface SchemeTemplate {
  id: string;
  companyId: string;
  orgUnitId: string;
  name: string;
  status: TemplateStatus;
  version: number;
  groupType: 'CASH' | 'COMMODITY' | 'DIGITAL';
  /** Paise (integer). */
  chitValue: number;
  subscriberCount: number;
  durationCycles: number;
  cycleFrequency: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  commissionRate: string;
  maxBidDiscountRate: string | null;
  penalRate: string | null;
  auctionType: 'OPEN' | 'CLOSED';
  dividendDistribute: 'CURRENT_MONTH' | 'NEXT_MONTH' | null;
  dividendCategory: 'ESTIMATE_BASED' | 'AUCTION_BASED' | 'DRAW_BASED' | null;
  wantedReportType: string | null;
  biddingWindowMinutes: number | null;
  preBidReminderLeadMinutes: number | null;
  bidExtensionWindowSecs: number | null;
  bidExtensionIntervalSecs: number | null;
  hasForemanTicket: boolean;
  foremanTicketIsPaying: boolean;
  fmPrDue: number | null;
  requiresSurety: boolean;
  maxSuretiesPerSubscriber: number;
  tdsApplicable: boolean;
  gstApplicable: boolean;
  tdsRate: string | null;
  isOnline: boolean;
  batchCount: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSchemeTemplatePayload {
  name: string;
  groupType: 'CASH' | 'COMMODITY' | 'DIGITAL';
  chitValue: number;
  subscriberCount: number;
  durationCycles: number;
  cycleFrequency: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  commissionRate: number;
  maxBidDiscountRate?: number;
  penalRate?: number;
  auctionType: 'OPEN' | 'CLOSED';
  dividendDistribute?: 'CURRENT_MONTH' | 'NEXT_MONTH';
  dividendCategory?: 'ESTIMATE_BASED' | 'AUCTION_BASED' | 'DRAW_BASED';
  wantedReportType?: 'ESTIMATE_BASED' | 'BATCH_BASED' | 'DRAW_BASED';
  biddingWindowMinutes?: number;
  preBidReminderLeadMinutes?: number;
  bidExtensionWindowSecs?: number;
  bidExtensionIntervalSecs?: number;
  hasForemanTicket: boolean;
  foremanTicketIsPaying: boolean;
  fmPrDue?: number;
  requiresSurety: boolean;
  maxSuretiesPerSubscriber: number;
  tdsApplicable: boolean;
  gstApplicable: boolean;
  tdsRate?: number;
  isOnline: boolean;
  batchCount?: number;
}

export const templateKeys = {
  all: ['scheme-templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (params?: Record<string, unknown>) => [...templateKeys.lists(), params] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
};

export function useTemplates(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: templateKeys.list(params),
    queryFn: async () => {
      const searchParams = params ? new URLSearchParams(params as unknown as Record<string, string>).toString() : '';
      return fetcher.get<{ data: SchemeTemplate[]; meta: Record<string, unknown> }>(`/staff/v1/chit-groups/templates?${searchParams}`);
    },
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: async () => fetcher.get<SchemeTemplate>(`/staff/v1/chit-groups/templates/${id}`),
    enabled: !!id,
  });
}

function invalidateTemplates(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  queryClient.invalidateQueries({ queryKey: templateKeys.all });
  if (id) queryClient.invalidateQueries({ queryKey: templateKeys.detail(id) });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSchemeTemplatePayload) =>
      fetcher.post<SchemeTemplate>('/staff/v1/chit-groups/templates', body),
    onSuccess: (_data, _vars) => invalidateTemplates(queryClient),
  });
}

export function useUpdateTemplate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateSchemeTemplatePayload>) =>
      fetcher.put<SchemeTemplate>(`/staff/v1/chit-groups/templates/${id}`, body),
    onSuccess: () => invalidateTemplates(queryClient, id),
  });
}

export function useActivateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetcher.post<SchemeTemplate>(`/staff/v1/chit-groups/templates/${id}/activate`, {}),
    onSuccess: (_data, vars) => invalidateTemplates(queryClient, vars.id),
  });
}

export function useRetireTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetcher.post<SchemeTemplate>(`/staff/v1/chit-groups/templates/${id}/retire`, {}),
    onSuccess: (_data, vars) => invalidateTemplates(queryClient, vars.id),
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetcher.delete<{ success: boolean }>(`/staff/v1/chit-groups/templates/${id}`),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}
